import { Query } from '@apollo/client/react/components';
import React from 'react';
import CONTENT_SOURCE_V1_QUERY from 'user/queries/contentSources.gql';
import type {
  ContentSources as SourcesV1,
  ContentSources_me_composer_contentSources as ContentSourcesV1,
} from 'user/queries/__generated__/ContentSources';
import CONTENT_SOURCE_V2_QUERY from 'user/queries/contentSourcesV2.gql';
import type {
  contentSourcesv2 as SourcesV2,
  contentSourcesv2_me_composer_contentSourcesInfinity as ContentSourcesV2,
} from 'user/queries/__generated__/contentSourcesv2';
import type { ContentSource } from '../components/AccountLinkingComposerSidebarServices';

import QUERY from '../queries/AccountLinkingComposerSidebar.gql';
import type {
  AccountLinkingComposerSidebarQuery as QueryData,
  AccountLinkingComposerSidebarQueryVariables as QueryVariables,
} from '../queries/__generated__/AccountLinkingComposerSidebarQuery';

export { QUERY };

export type Props = {
  fetchPolicy?: string;
  children: (props: {
    loading: boolean;
    error?: Error;
    sources: Array<ContentSource> | undefined | null;
    refetch: Function;
  }) => any;
};

function V2ToContentSource(
  v2: Array<ContentSourcesV2> | undefined
): Array<ContentSource> | undefined | null {
  if (!v2) {
    return null;
  }

  return v2.map((source) => {
    return {
      title: source.title,
      providerKey: source.providerKey,
      iconURL: source.iconURL,
      linkedAccountInfo: source.linkedAccountInfo
        ? {
            authUrl: source.linkedAccountInfo.authUrl,
            isLinked: source.linkedAccountInfo.isLinked,
            providerKey: source.linkedAccountInfo.providerKey,
          }
        : undefined,
    };
  });
}

function V1ToContentSource(
  v1: Array<ContentSourcesV1> | undefined
): Array<ContentSource> | undefined | null {
  if (!v1) {
    return null;
  }

  return v1.map((source) => {
    return {
      title: source.name,
      providerKey: source.source,
      iconURL: source.imageUrl,
      linkedAccountInfo: source.linkedAccount
        ? {
            authUrl: source.linkedAccount.authUrl,
            isLinked: source.linkedAccount.isLinked,
            providerKey: source.linkedAccount.provider,
          }
        : undefined,
    };
  });
}

function ContainerV1(props: Props) {
  return (
    <Query<SourcesV1>
      query={CONTENT_SOURCE_V1_QUERY}
      // @ts-expect-error apollo type is much more strict now
      fetchPolicy={props.fetchPolicy}
    >
      {({ loading, error, data, refetch }) => {
        return props.children({
          error,
          loading,
          sources: V1ToContentSource(data?.me?.composer?.contentSources),
          refetch,
        });
      }}
    </Query>
  );
}

function ContainerV2(props: Props) {
  return (
    <Query<SourcesV2>
      query={CONTENT_SOURCE_V2_QUERY}
      // @ts-expect-error apollo type is much more strict now
      fetchPolicy={props.fetchPolicy}
    >
      {({ loading, error, data, refetch }) => {
        return props.children({
          error,
          loading,
          sources: V2ToContentSource(data?.me?.composer?.contentSourcesInfinity),
          refetch,
        });
      }}
    </Query>
  );
}

const FEATURE = '2018.developer.whitelist';
const EXPERIMENT = '2018.apr.web.content_source_v2';

/* This container fetches the content sources.
 * We currently have an experiment with two queries.
 * We cast each query results into an common type of ContentSource.
 * This type is defined in the UI component: AccountLinkingComposerSidebarServices
 */
export default function AccountLinkingComposerSidebarServicesContainer(props: Props) {
  return (
    <Query<QueryData, QueryVariables>
      query={QUERY}
      variables={{ feature: FEATURE, experiment: EXPERIMENT }}
    >
      {({ loading, data }) => {
        if (loading) {
          return null;
        }

        return hasFeature(data, true) || hasExperiment(data, 'variant') ? (
          <ContainerV2 {...props} />
        ) : (
          <ContainerV1 {...props} />
        );
      }}
    </Query>
  );
}

function hasFeature(data: QueryData | undefined, value: boolean) {
  return data?.me?.feature === value;
}

function hasExperiment(data: QueryData | undefined, variation: string) {
  return data?.me?.experiments?.[0]?.variation === variation;
}
